import { join } from 'node:path';
import { appendJsonl, logExists, readJsonl } from './jsonl-log.js';
import { lessonsPaths } from './paths.js';
import { isTelemetryEnabled, sessionId } from './telemetry.js';

/**
 * The lesson-effectiveness substrate (EVALUATE) — the one gap no competitor
 * closes: did a delivered lesson actually prevent the repeat? An append-only
 * side-channel log (NEVER the graph, which is `.strict()` + frozen) records two
 * event kinds, and effectiveness is DERIVED at read time. Gated on the same
 * telemetry env as the recall log, so the default configuration pays nothing.
 */

const MAX_OUTCOME_LOG_RECORDS = 5000;
const OUTCOME_LOG_TRIM_TRIGGER_BYTES = 2_000_000;

/** A lesson injected into agent context for a concrete action. */
export interface OutcomeDelivered {
  /** ISO-8601 timestamp supplied by the caller (engine never reads the wall clock). */
  readonly ts: string;
  readonly kind: 'delivered';
  readonly lessonId: string;
  /** Normalized action+error signature — see context-key.ts. Never raw text. */
  readonly contextKey: string;
  readonly session?: string;
}

/** A failure observed at a decision point (the thing a lesson is meant to prevent). */
export interface OutcomeFailure {
  readonly ts: string;
  readonly kind: 'failure';
  readonly contextKey: string;
  /** Coarse class of the error (see error-class.ts) — a hint for STORE, not a key. */
  readonly errorClass?: string;
  readonly session?: string;
}

export type OutcomeEvent = OutcomeDelivered | OutcomeFailure;

/** Append-only outcome log, sibling of the graph — never the graph itself. */
export function outcomeLogPath(projectRoot: string): string {
  return join(lessonsPaths(projectRoot).base, 'outcome-log.jsonl');
}

/** Append one event — a no-op unless telemetry is enabled, so recall/capture pay nothing by default. */
export function appendOutcomeEvent(
  projectRoot: string,
  event: OutcomeEvent,
  env: NodeJS.ProcessEnv = process.env,
): void {
  if (!isTelemetryEnabled(env)) return;
  appendJsonl(outcomeLogPath(projectRoot), event, {
    maxRecords: MAX_OUTCOME_LOG_RECORDS,
    trimTriggerBytes: OUTCOME_LOG_TRIM_TRIGGER_BYTES,
  });
}

/** Read every outcome event, skipping torn lines. Returns [] when absent. */
export function readOutcomeLog(projectRoot: string): OutcomeEvent[] {
  return readJsonl<OutcomeEvent>(outcomeLogPath(projectRoot));
}

/** True when the outcome log file exists — distinguishes "never recorded" from "empty". */
export function outcomeLogExists(projectRoot: string): boolean {
  return logExists(outcomeLogPath(projectRoot));
}

/** Record that these lessons were injected for `contextKey` (stamps ts + session). No-op unless telemetry is on. */
export function recordDelivered(
  projectRoot: string,
  lessonIds: readonly string[],
  contextKey: string,
  env: NodeJS.ProcessEnv = process.env,
): void {
  if (!isTelemetryEnabled(env) || lessonIds.length === 0) return;
  const ts = new Date().toISOString();
  const session = sessionId(env);
  for (const lessonId of lessonIds) {
    appendOutcomeEvent(
      projectRoot,
      {
        ts,
        kind: 'delivered',
        lessonId,
        contextKey,
        ...(session !== undefined ? { session } : {}),
      },
      env,
    );
  }
}

/** Record a failure observed for `contextKey` (stamps ts + session). No-op unless telemetry is on. */
export function recordFailure(
  projectRoot: string,
  contextKey: string,
  errorClass?: string,
  env: NodeJS.ProcessEnv = process.env,
): void {
  if (!isTelemetryEnabled(env)) return;
  const session = sessionId(env);
  appendOutcomeEvent(
    projectRoot,
    {
      ts: new Date().toISOString(),
      kind: 'failure',
      contextKey,
      ...(errorClass !== undefined ? { errorClass } : {}),
      ...(session !== undefined ? { session } : {}),
    },
    env,
  );
}

/** How many times `contextKey` has failed, plus the most recent error class. */
export interface ContextFailures {
  readonly count: number;
  readonly lastErrorClass?: string;
}

/**
 * Failure history for one action, for the recurrence-driven capture nudge (STORE).
 * A pure read over the log; `count` counts every failure recorded for `contextKey`
 * across sessions, so a mistake repeating over days is still seen as recurring.
 */
export function failuresForContext(projectRoot: string, contextKey: string): ContextFailures {
  let count = 0;
  let lastErrorClass: string | undefined;
  for (const ev of readOutcomeLog(projectRoot)) {
    if (ev.kind !== 'failure' || ev.contextKey !== contextKey) continue;
    count += 1;
    if (ev.errorClass !== undefined) lastErrorClass = ev.errorClass;
  }
  return { count, ...(lastErrorClass !== undefined ? { lastErrorClass } : {}) };
}

/** Delivered/missed tallies for one lesson, derived from the event stream. */
export interface LessonOutcome {
  readonly delivered: number;
  readonly missed: number;
}

/** Scope by session so a failure only impeaches deliveries within its own session. */
function scopeKey(ev: { session?: string; contextKey: string }): string {
  return `${ev.session ?? ''} ${ev.contextKey}`;
}

/**
 * Effectiveness substrate: a delivered lesson is a MISS when the same
 * `(session, contextKey)` fails again LATER in the stream — it fired but did not
 * prevent the repeat. Pure and deterministic, O(n): index the last failure per
 * scope, then a delivery positioned before that index is a miss. The signal is
 * deliberately coarse (attribution is noisy) — an honest weak signal, not a fake
 * precise one.
 */
export function effectiveness(events: readonly OutcomeEvent[]): Map<string, LessonOutcome> {
  const lastFailure = new Map<string, number>();
  events.forEach((ev, i) => {
    if (ev.kind === 'failure') lastFailure.set(scopeKey(ev), i);
  });
  const out = new Map<string, { delivered: number; missed: number }>();
  events.forEach((ev, i) => {
    if (ev.kind !== 'delivered') return;
    const cur = out.get(ev.lessonId) ?? { delivered: 0, missed: 0 };
    cur.delivered += 1;
    const lf = lastFailure.get(scopeKey(ev));
    if (lf !== undefined && lf > i) cur.missed += 1;
    out.set(ev.lessonId, cur);
  });
  return out;
}

/** 1 = always helped, 0 = never helped. Undelivered lessons are neutral (1). */
export function effectivenessScore(o: LessonOutcome): number {
  return o.delivered === 0 ? 1 : 1 - o.missed / o.delivered;
}

/**
 * Per-lesson effectiveness score in [0,1] for the recall ranker. Empty when
 * telemetry is off or no lesson has been delivered — in which case ranking is
 * unchanged (every lesson is neutral).
 */
export function loadEffectiveness(projectRoot: string): ReadonlyMap<string, number> {
  const map = new Map<string, number>();
  for (const [id, o] of effectiveness(readOutcomeLog(projectRoot))) {
    map.set(id, effectivenessScore(o));
  }
  return map;
}
